import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { AuthProvider, useAuth, DrawerProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import Login from './components/Login/Login';
import Layout from './components/Layout/Layout';
import Viewer from './components/Viewer/Viewer';
import Workflow from './components/Workflow/Workflow';
import NotificationCenter from './components/NotificationCenter/NotificationCenter';
import GoMetadata from './components/GoMetadata/GoMetadata';
import SapAnalytics from './components/SapAnalytics/SapAnalytics';
import SapIntegration from './components/SapIntegration/SapIntegration';
import DocumentViewer from './components/DocumentViewer/DocumentViewer';
import Users from './components/Users/Users';
import Groups from './components/Groups/Groups';
import Permissions from './components/Permissions/Permissions';
import AdminDashboard from './components/AdminDashboard/AdminDashboard';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
});

// Component to handle parameter saving before any redirects
const ParameterSaver: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();

  // Function to parse XML and extract parameters - SIMPLE VERSION! 😄
  const parseXmlAndExtractParams = (xmlString: string): string => {
    console.log('🔍 Raw XML string:', xmlString.substring(0, 200) + '...');
    console.log('🔍 FULL Raw XML string:', xmlString);
    
    // First extract the URL from the XML
    const urlMatch = xmlString.match(/url="([^"]+)"/);
    let urlString = '';
    if (urlMatch && urlMatch[1]) {
      urlString = urlMatch[1];
      console.log('🔍 Extracted URL from XML:', urlString);
      console.log('🔍 URL length:', urlString.length);
      console.log('🔍 URL contains docId?', urlString.includes('docId'));
      console.log('🔍 URL contains pVersion?', urlString.includes('pVersion'));
      console.log('🔍 URL contains contRep?', urlString.includes('contRep'));
      console.log('🔍 URL contains accessMode?', urlString.includes('accessMode'));
      console.log('🔍 URL contains authId?', urlString.includes('authId'));
    } else {
      console.log('❌ No URL found in XML');
    }
    
    // Extract values from the URL string using regex
    console.log('🔍 Starting parameter extraction from URL...');
    const pVersionMatch = urlString.match(/pVersion=(\w+)/);
    const contRepMatch = urlString.match(/contRep=(\w+)/);
    const docIdMatch = urlString.match(/docId=(\w+)/);
    const accessModeMatch = urlString.match(/accessMode=(\w+)/);
    const authIdMatch = urlString.match(/authId=([^&"]+)/);
    
    // Check if 'get' parameter exists (it might be just 'get' without value)
    const hasGet = urlString.includes('get&') || urlString.includes('get=');
    
    console.log('🔍 Direct extraction results:');
    console.log('   hasGet:', hasGet);
    console.log('   pVersion:', pVersionMatch?.[1]);
    console.log('   contRep:', contRepMatch?.[1]);
    console.log('   docId:', docIdMatch?.[1]);
    console.log('   accessMode:', accessModeMatch?.[1]);
    console.log('   authId:', authIdMatch?.[1]);
    
    // Build the normalized parameters
    const essentialParams = new URLSearchParams();
    
    // Add 'get' parameter only if it exists (without =)
    if (hasGet) {
      essentialParams.append('get', '');
    }
    
    if (pVersionMatch?.[1]) {
      essentialParams.append('pVersion', pVersionMatch[1]);
    }
    
    if (contRepMatch?.[1]) {
      essentialParams.append('contRep', contRepMatch[1]);
    }
    
    if (docIdMatch?.[1]) {
      essentialParams.append('docId', docIdMatch[1]);
    }
    
    if (accessModeMatch?.[1]) {
      essentialParams.append('accessMode', accessModeMatch[1]);
    }
    
    if (authIdMatch?.[1]) {
      essentialParams.append('authId', authIdMatch[1]);
    }
    
    const normalizedParams = essentialParams.toString();
    console.log('🔍 Final normalized parameters:', normalizedParams);
    return normalizedParams;
  };

  // Function to extract essential parameters from direct URL
  const extractEssentialParams = (searchParams: URLSearchParams): string => {
    const essentialParams = new URLSearchParams();
    const essentialKeys = ['get', 'pVersion', 'contRep', 'docId', 'accessMode', 'authId'];
    
    essentialKeys.forEach(key => {
      const value = searchParams.get(key);
      if (value) {
        essentialParams.append(key, value);
        console.log(`🔍 Found direct param ${key}:`, value);
      }
    });
    
    const normalizedParams = essentialParams.toString();
    console.log('🔍 Normalized parameters from direct URL:', normalizedParams);
    return normalizedParams;
  };

  useEffect(() => {
    console.log('🔍 === PARAMETER SAVER ANALYSIS ===');
    console.log('🔍 Current pathname:', location.pathname);
    console.log('🔍 Current search:', location.search);
    console.log('🔍 User logged in?', !!user);
    console.log('🔍 User object:', user);
    
    const isDocumentViewerRoute = location.pathname.includes('/document-viewer');
    const hasXml = location.search.includes('xml=');
    const hasDirectParams = location.search.includes('docId=') || 
                           location.search.includes('get&') || 
                           location.search.includes('pVersion=');
    
    console.log('🔍 Route analysis:');
    console.log('   📁 Is document-viewer route?', isDocumentViewerRoute);
    console.log('   📄 Has XML param?', hasXml);
    console.log('   🔗 Has direct params?', hasDirectParams);
    
    // Check if we should process parameters
    if (isDocumentViewerRoute && (hasXml || hasDirectParams)) {
      console.log('🔍 === PROCESSING PARAMETERS ===');
      
      let normalizedParams = '';
      let shouldSaveUrl = false;
      
      if (hasXml) {
        console.log('🔍 Processing XML parameters...');
        const xmlParam = new URLSearchParams(location.search).get('xml');
        if (xmlParam) {
          normalizedParams = parseXmlAndExtractParams(xmlParam);
          if (normalizedParams) {
            shouldSaveUrl = true;
          } else {
            // If XML parsing fails, save the original URL as fallback
            console.log('⚠️ XML parsing failed, saving original URL as fallback');
            const originalUrl = location.pathname + location.search;
            sessionStorage.setItem('redirectAfterLogin', originalUrl);
            localStorage.setItem('redirectAfterLogin', originalUrl);
            console.log('💾 Saved original URL as fallback:', originalUrl);
            shouldSaveUrl = true;
          }
        }
      } else if (hasDirectParams) {
        console.log('🔍 Processing direct parameters...');
        const searchParams = new URLSearchParams(location.search);
        normalizedParams = extractEssentialParams(searchParams);
        if (normalizedParams) {
          shouldSaveUrl = true;
        }
      }
      
      if (shouldSaveUrl && normalizedParams) {
        console.log('💾 === SAVING NORMALIZED PARAMETERS ===');
        console.log('💾 Normalized parameters:', normalizedParams);
        
        // Create normalized URL
        const normalizedUrl = `/document-viewer?${normalizedParams}`;
        console.log('💾 Normalized URL:', normalizedUrl);
        
        // Clear any existing redirect URL first
        sessionStorage.removeItem('redirectAfterLogin');
        localStorage.removeItem('redirectAfterLogin');
        
        // Save normalized URL
        sessionStorage.setItem('redirectAfterLogin', normalizedUrl);
        localStorage.setItem('redirectAfterLogin', normalizedUrl);
        
        console.log('💾 Saved normalized URL to sessionStorage:', sessionStorage.getItem('redirectAfterLogin'));
        console.log('💾 Saved normalized URL to localStorage:', localStorage.getItem('redirectAfterLogin'));
        console.log('💾 SessionStorage length:', sessionStorage.length);
        console.log('💾 LocalStorage length:', localStorage.length);
        
        // Show all sessionStorage keys
        console.log('💾 All sessionStorage keys:');
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key) {
            console.log(`   ${key}: ${sessionStorage.getItem(key)}`);
          }
        }
        
        console.log('💾 === NORMALIZED PARAMETERS SAVED SUCCESSFULLY ===');
      } else if (!shouldSaveUrl) {
        console.log('❌ No essential parameters found in XML or direct params');
      }
    } else {
      console.log('❌ Not a document-viewer route or no important parameters found');
    }
    
    console.log('🔍 === END PARAMETER SAVER ANALYSIS ===');
  }, [location, user]);

  return null; // This component doesn't render anything
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <DrawerProvider>
          <Router>
            <ParameterSaver />
            <Routes>
            {/* Public route for login */}
            <Route path="/login" element={<Login />} />
            
            {/* Document viewer route - standalone for SAP URLs */}
            <Route 
              path="/document-viewer" 
              element={
                <ProtectedRoute requireDocumentSession={true}>
                  <DocumentViewer />
                </ProtectedRoute>
              } 
            />
            
            {/* Protected routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/viewer" replace />} />
              <Route path="viewer" element={<Viewer />} />
              <Route path="workflow" element={<Workflow />} />
              <Route path="notifications" element={<NotificationCenter />} />
              <Route path="go-metadata" element={<GoMetadata />} />
              <Route path="sap-analytics" element={<SapAnalytics />} />
              <Route path="sap-integration" element={<SapIntegration />} />
              <Route path="dashboard" element={
                <ProtectedRoute requireAdmin={true}>
                  <AdminDashboard />
                </ProtectedRoute>
              } />
              <Route path="users" element={
                <ProtectedRoute requireAdmin={true}>
                  <Users />
                </ProtectedRoute>
              } />
              <Route path="groups" element={
                <ProtectedRoute requireAdmin={true}>
                  <Groups />
                </ProtectedRoute>
              } />
              <Route path="permissions" element={
                <ProtectedRoute requireAdmin={true}>
                  <Permissions />
                </ProtectedRoute>
              } />
            </Route>
            
            {/* Catch all route - redirect to login */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </Router>
        </DrawerProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App; 